import { toast } from 'sonner';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import { LineChart, Line, XAxis, YAxis, Tooltip } from 'recharts';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import LoanDocuments from './LoanDocuments';
import api from '../api';

function useContainerWidth(ref) {
  const [width, setWidth] = useState(300);
  useEffect(() => {
    const update = () => { if (ref.current) setWidth(ref.current.offsetWidth - 32); };
    update();
    window.addEventListener('resize', update);
    const timer = setTimeout(update, 200);
    return () => { window.removeEventListener('resize', update); clearTimeout(timer); };
  }, [ref]);
  return width;
}

// Helper function for European number format
const formatEuro = (amount) => {
  if (!amount) return '0€';
  const num = parseFloat(amount);
  return num.toLocaleString('es-ES', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }) + '€';
};

function LoanDetail() {
  const { loanId } = useParams();
  const [loan, setLoan] = useState(null);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const projChartRef = useRef(null);
  const projChartWidth = useContainerWidth(projChartRef);
  const { user } = useAuth();

  useEffect(() => {
    fetchLoanDetails();
  }, [loanId]);

  const fetchLoanDetails = async () => {
    try {
      const loanRes = await api.get(`/api/loans/${loanId}`);
      setLoan(loanRes.data);
      
      if (loanRes.data.contract_id) {
        const contractsRes = await api.get('/api/contracts', {
          params: { loan_id: loanId }
        });
        if (contractsRes.data.length > 0) {
          setContract(contractsRes.data[0]);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateProjectionData = () => {
    if (!loan) return [];
    
    const data = [];
    const monthlyInterest = (loan.capital * loan.interest_rate) / (12 * 100);
    
    for (let month = 0; month <= loan.duration_months; month++) {
      data.push({
        month: month,
        accumulated: monthlyInterest * month
      });
    }
    return data;
  };

  const getProgressPercentage = () => {
    if (!loan) return 0;
    const totalDays = loan.duration_months * 30;
    const daysRemaining = loan.days_remaining || 0;
    const elapsed = totalDays - daysRemaining;
    
    // Ensure we never show negative progress
    if (elapsed < 0) return 0;
    
    return Math.min(100, Math.max(0, (elapsed / totalDays) * 100));
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }
  
  if (!loan) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area">
          <TopBar title={t('loanDetail.title')} />
          <div style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
            {t('loanDetail.notFound')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar title={t('loanDetail.title')} />

        <main style={{ 
          padding: '1.5rem', 
          maxWidth: '1400px', 
          margin: '0 auto',
          '@media (maxWidth: 768px)': {
            padding: '1rem'
          }
        }} className="fade-in">
          
          {/* Header Card */}
          <div className="dark-card" style={{ 
            marginBottom: '1.5rem',
            padding: '1.5rem'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                <h2 style={{ 
                  fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', 
                  fontWeight: '700', 
                  color: '#60a5fa', 
                  marginBottom: '0.5rem',
                  wordBreak: 'break-word'
                }}>
                  {formatEuro(loan.capital)}
                </h2>
                <p style={{ 
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                }}>
                  {t('loanDetail.loanAt')} {loan.interest_rate}% {t('loanDetail.annual')}
                </p>
              </div>
              <span className="status-badge" style={{
                fontSize: 'clamp(0.8125rem, 1.5vw, 0.9375rem)',
                whiteSpace: 'nowrap',
                ...(loan.status === 'cancelled' ? { background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' } :
                    loan.status === 'matured' ? { background: 'rgba(107,114,128,0.15)', color: '#9ca3af' } : {})
              }}>
                {loan.status === 'active' ? '✓ ' + t('status.active') : 
                 loan.status === 'cancelled' ? t('status.cancelled') :
                 loan.status === 'matured' ? t('status.matured') :
                 '⏳ ' + t('status.pending')}
              </span>
            </div>
          </div>

          {/* Metrics Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem', 
            marginBottom: '1.5rem'
          }}>
            {[
              { label: t('loanDetail.interestGenerated'), value: formatEuro(loan.interest_generated || 0), color: '#34d399' },
              { label: t('loanDetail.estimatedTotalReturn'), value: formatEuro(loan.total_return || 0), color: '#60a5fa' },
              { label: t('loanDetail.remainingDays'), value: loan.days_remaining || 0, color: '#fb923c' },
              { label: t('loanDetail.dailyInterest'), value: formatEuro((loan.capital * loan.interest_rate) / (365 * 100)), color: '#c084fc' }
            ].map(({ label, value, color }) => (
              <div key={label} className="dark-card" style={{ 
                padding: '1.25rem',
                minWidth: '150px'
              }}>
                <p style={{ 
                  fontSize: 'clamp(0.75rem, 1.5vw, 0.8125rem)', 
                  color: 'rgba(255,255,255,0.5)', 
                  marginBottom: '0.5rem',
                  wordBreak: 'break-word'
                }}>
                  {label}
                </p>
                <p style={{ 
                  fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)', 
                  fontWeight: '700', 
                  color: color,
                  wordBreak: 'break-word'
                }}>
                  {value}
                </p>
              </div>
            ))}
          </div>

          {/* Timeline */}
          <div className="dark-card" style={{ 
            marginBottom: '1.5rem',
            padding: '1.5rem'
          }}>
            <h3 style={{ 
              fontSize: 'clamp(1rem, 2vw, 1.05rem)', 
              fontWeight: '600', 
              color: 'white', 
              marginBottom: '1.25rem' 
            }}>
              {t('loanDetail.loanTimeline')}
            </h3>
            <div style={{ position: 'relative' }}>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                background: 'rgba(255,255,255,0.1)', 
                borderRadius: '10px', 
                overflow: 'hidden' 
              }}>
                <div style={{
                  width: `${getProgressPercentage()}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #34d399 0%, #10b981 100%)',
                  borderRadius: '10px',
                  transition: 'width 0.5s ease'
                }}></div>
              </div>
              <p style={{ 
                marginTop: '0.75rem', 
                fontSize: 'clamp(0.8125rem, 1.5vw, 0.875rem)', 
                color: 'rgba(255,255,255,0.6)' 
              }}>
                {getProgressPercentage().toFixed(1)}% {t('loanDetail.completed')}
              </p>
            </div>
          </div>

          {/* Projection Chart */}
          <div className="dark-card" style={{ 
            marginBottom: '1.5rem',
            padding: '1.5rem',
            overflow: 'hidden'
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'white', marginBottom: '0.75rem' }}>
              {t('loanDetail.accumulatedInterestProjection')}
            </h3>
            <div ref={projChartRef}>
              <LineChart width={projChartWidth} height={250} data={generateProjectionData()} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <XAxis dataKey="month" stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} />
                <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} width={55} />
                <Tooltip 
                  contentStyle={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: '#34d399' }}
                  formatter={(value) => formatEuro(value)}
                />
                <Line type="monotone" dataKey="accumulated" stroke="#34d399" strokeWidth={2} dot={{ fill: '#34d399', r: 3 }} name={t('loanDetail.interestGenerated')} />
              </LineChart>
            </div>
          </div>

          {/* Cancellation Summary - for cancelled loans */}
          {loan.status === 'cancelled' && loan.cancellation_request && (
            <div className="dark-card" style={{ 
              marginBottom: '1.5rem', padding: '1.5rem',
              border: '2px solid rgba(239,68,68,0.4)',
              background: 'rgba(239,68,68,0.08)'
            }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#ef4444', marginBottom: '1rem' }}>
                {i18n.language === 'ca' ? 'Préstec Cancel·lat Anticipadament' : 'Préstamo Cancelado Anticipadamente'}
              </h3>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>{i18n.language === 'ca' ? 'Tipus Original' : 'Tipo Original'}</span>
                  <span style={{ fontWeight: '700', color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through' }}>{loan.cancellation_request.original_interest_rate}%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>{i18n.language === 'ca' ? 'Penalització' : 'Penalización'}</span>
                  <span style={{ fontWeight: '700', color: '#ef4444' }}>-2.5%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'rgba(239,68,68,0.1)', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.3)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>{i18n.language === 'ca' ? 'Tipus Aplicat Final' : 'Tipo Aplicado Final'}</span>
                  <span style={{ fontWeight: '700', color: '#fbbf24' }}>{loan.cancellation_request.adjusted_interest_rate || loan.final_interest_rate}%</span>
                </div>
                {loan.final_interest_amount && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'rgba(59,130,246,0.1)', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.3)' }}>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>{i18n.language === 'ca' ? 'Interessos Bruts' : 'Intereses Brutos'}</span>
                    <span style={{ fontWeight: '700', color: '#60a5fa', fontSize: '1.1rem' }}>€{loan.final_interest_amount.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                )}
                {loan.final_interest_retention && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'rgba(239,68,68,0.06)', borderRadius: '8px' }}>
                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>{i18n.language === 'ca' ? 'Retenció IRPF (19%)' : 'Retención IRPF (19%)'}</span>
                    <span style={{ fontWeight: '600', color: '#ef4444' }}>-€{loan.final_interest_retention.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                )}
                {loan.final_interest_net && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: 'rgba(16,185,129,0.1)', borderRadius: '8px', border: '1px solid rgba(16,185,129,0.3)' }}>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>{i18n.language === 'ca' ? 'Interessos Nets a Cobrar' : 'Intereses Netos a Cobrar'}</span>
                    <span style={{ fontWeight: '700', color: '#10b981', fontSize: '1.1rem' }}>€{loan.final_interest_net.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                )}
                {loan.cancellation_request.executed_at && (
                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.25rem' }}>
                    {i18n.language === 'ca' ? 'Executada el' : 'Ejecutada el'} {new Date(loan.cancellation_request.executed_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Contract Info */}
          {/* Contract Info */}
          {contract && (
            <div className="dark-card" style={{ 
              padding: '1.5rem'
            }}>
              <h3 style={{ 
                fontSize: 'clamp(1rem, 2vw, 1.05rem)', 
                fontWeight: '600', 
                color: 'white', 
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>📄</span>
                <span>{t('loanDetail.contractInfo')}</span>
              </h3>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                  <p style={{ 
                    fontSize: 'clamp(0.8125rem, 1.5vw, 0.875rem)', 
                    color: 'rgba(255,255,255,0.6)',
                    wordBreak: 'break-all',
                    lineHeight: '1.6'
                  }}>
                    <strong>ID:</strong> {contract._id}
                  </p>
                  <p style={{ 
                    fontSize: 'clamp(0.8125rem, 1.5vw, 0.875rem)', 
                    color: 'rgba(255,255,255,0.6)',
                    marginTop: '0.5rem'
                  }}>
                    <strong>{t('loanDetail.language')}:</strong> {contract.language.toUpperCase()}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => navigate('/contracts')}
                    className="btn-dark-secondary"
                    style={{ 
                      padding: '0.75rem 1.5rem',
                      fontSize: 'clamp(0.8125rem, 1.5vw, 0.875rem)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {t('loanDetail.viewContract')}
                  </button>
                  {contract._id && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const response = await api.get(`/api/contracts/${contract._id}/download`, {
                            responseType: 'blob'
                          });
                          
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `contracte_${contract._id}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (error) {
                          toast.error('Error en descarregar el contracte: ' + (error.response?.data?.detail || 'Error'));
                        }
                      }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                        color: 'white',
                        border: 'none',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: 'clamp(0.8125rem, 1.5vw, 0.875rem)',
                        transition: 'transform 0.2s ease',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      📥 {t('common.download')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Loan Documents Section */}
          <LoanDocuments loanId={loan._id} isAdmin={user?.role === 'admin'} />
          
          {/* Early Cancellation Section - discreet, at bottom */}
          {loan.status === 'active' && (
            <div style={{ 
              marginTop: '2rem',
              padding: '1rem 1.25rem',
              border: '1px solid rgba(255,255,255,0.08)', 
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.02)'
            }}>
              {loan.cancellation_request && loan.cancellation_request.status === 'pending' ? (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '0.85rem', color: '#fb923c' }}>
                    ⏳ {t('loanDetail.cancellationInProgress')}
                  </p>
                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                    {t('loanDetail.cancellationInProgressText')}
                  </p>
                </div>
              ) : loan.cancellation_request && loan.cancellation_request.status === 'approved' ? (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '0.85rem', color: '#fbbf24' }}>
                    {i18n.language === 'ca' ? "La teva sol·licitud de cancel·lació ha estat aprovada. S'executarà quan sigui possible." : 'Tu solicitud de cancelación ha sido aprobada. Se ejecutará cuando sea posible.'}
                  </p>
                </div>
              ) : loan.cancellation_request && loan.cancellation_request.status === 'rejected' ? (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.5rem' }}>
                    {t('loanDetail.cancellationRejectedText')}
                  </p>
                  <button
                    onClick={async () => {
                      if (window.confirm(`${t('loanDetail.requestCancellation')} de nou?`)) {
                        try {
                          await api.post(`/api/loans/${loan._id}/request-cancellation`);
                          toast.success('Sol·licitud rebuda correctament.');
                          await fetchLoanDetails();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Error');
                        }
                      }
                    }}
                    style={{ padding: '0.5rem 1rem', background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', cursor: 'pointer' }}
                  >
                    {i18n.language === 'ca' ? 'Tornar a sol·licitar cancel·lació' : 'Volver a solicitar cancelación'}
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <button
                    onClick={async () => {
                      const confirmText = `${t('loanDetail.requestCancellation')}?\n\n${t('loanDetail.penalty')}: ${t('loanDetail.penaltyText', { from: loan.interest_rate, to: loan.interest_rate - 2.5 })}`;
                      if (window.confirm(confirmText)) {
                        try {
                          await api.post(`/api/loans/${loan._id}/request-cancellation`);
                          toast.success(t('loanDetail.requestReceived'));
                          await fetchLoanDetails();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || t('errors.unknown'));
                        }
                      }
                    }}
                    style={{ padding: '0.5rem 1rem', background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', cursor: 'pointer' }}
                  >
                    {t('loanDetail.requestCancellation')}
                  </button>
                  <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)', marginTop: '0.4rem' }}>
                    {i18n.language === 'ca' ? 'Penalització: -2,5% sobre el tipus pactat' : 'Penalización: -2,5% sobre el tipo pactado'}
                  </p>
                </div>
              )}
            </div>
          )}
          
        </main>
      </div>
    </div>
  );
}

export default LoanDetail;
